import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Modal, Input, Select, Spin } from "antd";
import { NotificationManager } from "react-notifications";
import CompanyLocationService from "../../services/company-location.service";

const { Option } = Select;

// DataSet->JSON serialization on this API renders any NULL column as {} instead of null —
// this affects old columns too (CreatedBy/CreatedDate on legacy rows), not just new ones.
const safe = (v) => (v && typeof v === "object" ? "" : v ?? "");

// Matches the prototype's .field label style exactly (Diamond-PO-Prototype-v3.html):
// font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:#6b7689; font-weight:600
const fieldLabelStyle = {
  display: "block",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: ".5px",
  color: "#6b7689",
  fontWeight: 600,
  marginBottom: 5,
};

// Addresses screen — matches Diamond-PO-Prototype-v3.html's addrPage()/addrForm() exactly.
// Shared by both roles: a Retailer sees Receiving first (memos/purchases are delivered there),
// a Supplier sees Shipping first (memos/orders leave from there).

const emptyForm = {
  Label: "",
  ContactName: "",
  Address: "",
  City: "",
  State: "",
  Zip: "",
  Country: "United States",
  Phone: "",
};

const isTrue = (v) => v === true || v === 1 || v === "1";

export const Addresses = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [dealerMembershipType, setDealerMembershipType] = useState("");
  const [locations, setLocations] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalKind, setModalKind] = useState("RECEIVING");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      setDealerMembershipType(loginDetials.loginDetials.responseData.dealerMemberTypeName);
      loadLocations(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRetailer = dealerMembershipType === "Retailer" || dealerMembershipType === "Combined";

  const loadLocations = (id) => {
    setLoading(true);
    CompanyLocationService.GetPOLocationDetails({ DealerID: id })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData?.Table) {
          // Only DealersPOLocation-sourced rows, only active ones (soft-deleted stay hidden)
          const rows = response.data.responseData.Table.filter(
            (r) => r.LocationType === "LocationId2" && isTrue(r.IsActive)
          );
          setLocations(rows);
          setCountryList(response.data.responseData.Country || []);
        } else {
          setLocations([]);
        }
      })
      .catch(() => {
        setLoading(false);
        setLocations([]);
      });
  };

  const addrsOfKind = (kind) => {
    const list = locations.filter((l) => l.AddressKind === kind);
    return [...list].sort((a, b) => (isTrue(b.IsDefault) ? 1 : 0) - (isTrue(a.IsDefault) ? 1 : 0));
  };

  const openAdd = (kind) => {
    setModalKind(kind);
    setEditingId(null);
    setForm(emptyForm);
    setModalVisible(true);
  };

  const openEdit = (kind, loc) => {
    setModalKind(kind);
    setEditingId(loc.LocationId);
    setForm({
      Label: safe(loc.LocationName) || "",
      ContactName: safe(loc.ContactName) || "",
      Address: safe(loc.Address) || "",
      City: safe(loc.City) || "",
      State: safe(loc.State) || "",
      Zip: safe(loc.Zip) || "",
      Country: safe(loc.Country) || "United States",
      Phone: safe(loc.Phone) || "",
    });
    setModalVisible(true);
  };

  const saveDetails = (locationId) => {
    CompanyLocationService.UpdatePOLocation({
      txtAddress: form.Address,
      txtCity: form.City,
      txtState: form.State,
      txtZip: form.Zip,
      drpcountry: form.Country,
      txtPhone: form.Phone,
      txtEmail: "",
      txtWebsite: "",
      ContactName: form.ContactName,
      DealerID: dealerId.toString(),
      LocationID: locationId.toString(),
    })
      .then((response) => {
        setSaving(false);
        if (response.data.message === "Success") {
          NotificationManager.success(t("Address saved"));
          setModalVisible(false);
          loadLocations(dealerId);
        } else {
          NotificationManager.error(t("Could not save address"));
        }
      })
      .catch(() => setSaving(false));
  };

  const handleSave = () => {
    if (!form.Address || !form.City || !form.Zip || !form.ContactName) {
      NotificationManager.error(t("Street, city, postcode and contact are required"));
      return;
    }
    setSaving(true);
    if (editingId) {
      saveDetails(editingId);
    } else {
      CompanyLocationService.UpdatePOLocationName({
        DealerID: dealerId.toString(),
        txtAddLocation:
          form.Label || (modalKind === "RECEIVING" ? t("Receiving address") : t("Shipping address")),
        AddressKind: modalKind,
      })
        .then((addResponse) => {
          if (addResponse.data.message === "Success") {
            saveDetails(addResponse.data.responseData);
          } else {
            setSaving(false);
            NotificationManager.error(t("Could not save address"));
          }
        })
        .catch(() => setSaving(false));
    }
  };

  const handleMakeDefault = (loc) => {
    CompanyLocationService.SetDefaultPOLocation({
      DealerID: dealerId.toString(),
      LocationID: loc.LocationId.toString(),
    }).then((response) => {
      if (response.data.message === "Success") {
        NotificationManager.success(t("Default address updated"));
        loadLocations(dealerId);
      } else {
        NotificationManager.error(t("Could not set default"));
      }
    });
  };

  const handleRemove = (loc) => {
    Modal.confirm({
      title: t("Remove this address?"),
      content: `${safe(loc.LocationName)}, ${safe(loc.City)}. ${t(
        "Documents already issued keep their copy of it."
      )}`,
      okText: t("Remove"),
      okButtonProps: { danger: true },
      onOk: () =>
        CompanyLocationService.DeletePOLocation({
          DealerID: dealerId.toString(),
          LocationID: loc.LocationId.toString(),
        }).then((response) => {
          if (response.data.message === "Success") {
            NotificationManager.success(t("Address removed"));
            loadLocations(dealerId);
          } else {
            NotificationManager.error(t("Could not remove address"));
          }
        }),
    });
  };

  const noneOnFileText = (kind) => {
    if (kind === "RECEIVING") {
      return isRetailer
        ? t("You cannot send a memo request or place a purchase order until you add one.")
        : t("Retailers cannot ship memo stones back to you, and discrepancy returns are blocked.");
    }
    return isRetailer
      ? t("You cannot ship memo stones back to a supplier until you add one.")
      : t("You cannot ship memos or confirm purchase orders until you add one.");
  };

  const renderColumn = (kind, title, why) => {
    const list = addrsOfKind(kind);
    return (
      <div className="panel" style={{ flex: 1, minWidth: 320, background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0 }}>{title}</h3>
            <span style={{ color: "#888", fontSize: 13 }}>{why}</span>
          </div>
          <Button size="small" onClick={() => openAdd(kind)}>
            + {t("Add")}
          </Button>
        </div>
        {list.length ? (
          list.map((a, i) => (
            <div
              key={a.LocationId}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                marginBottom: 10,
                border: "1px solid #eee",
                borderRadius: 6,
                padding: 12,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    color: "#6b7689",
                    fontWeight: 700,
                    marginBottom: 2,
                  }}
                >
                  {i === 0 ? t("Default") : t("Alternate")}
                </div>
                <div>
                  <b>{safe(a.LocationName)}</b>
                </div>
                <div>{safe(a.ContactName)}</div>
                <div>{safe(a.Address)}</div>
                <div>
                  {safe(a.City)}, {safe(a.State)} {safe(a.Zip)} · {safe(a.Country)}
                </div>
                <div style={{ color: "#888" }}>{safe(a.Phone)}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {i !== 0 && (
                  <Button size="small" onClick={() => handleMakeDefault(a)}>
                    {t("Make default")}
                  </Button>
                )}
                <Button size="small" onClick={() => openEdit(kind, a)}>
                  {t("Edit")}
                </Button>
                <Button size="small" danger onClick={() => handleRemove(a)}>
                  {t("Remove")}
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div style={{ background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 6, padding: 12 }}>
            <b>{t("None on file")}</b>
            <div style={{ fontSize: 13, marginTop: 4 }}>{noneOnFileText(kind)}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="addresses-page" style={{ padding: 20 }}>
      <Spin spinning={loading}>
        <h2>{t("Addresses")}</h2>
        <p style={{ color: "#888" }}>{t("Where stones leave from and arrive at")}</p>
        <div style={{ background: "#e6f4ff", border: "1px solid #91caff", borderRadius: 6, padding: 14, marginBottom: 16 }}>
          <b>{t("Two kinds of address, and every document freezes a copy")}</b>
          <div style={{ marginTop: 6 }}>
            <b>{t("Shipping")}</b> {t("is where you send parcels from.")} <b>{t("Receiving")}</b>{" "}
            {t("is where you take delivery.")}{" "}
            {t(
              "A memo or purchase order is only created when both ends exist, and it stores a snapshot — editing an address later never rewrites a document already issued."
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {isRetailer ? (
            <>
              {renderColumn("RECEIVING", t("Receiving addresses"), t("memos and purchases are delivered here"))}
              {renderColumn("SHIPPING", t("Shipping addresses"), t("returns leave from here"))}
            </>
          ) : (
            <>
              {renderColumn("SHIPPING", t("Shipping addresses"), t("memos and orders leave from here"))}
              {renderColumn("RECEIVING", t("Receiving addresses"), t("returns come back here"))}
            </>
          )}
        </div>
      </Spin>

      <Modal
        className="addr-modal"
        title={null}
        closable={false}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={760}
        bodyStyle={{ padding: 0 }}
      >
        <div className="addr-mh">
          <h3>
            {(editingId ? t("Edit") : t("Add")) +
              " " +
              (modalKind === "RECEIVING" ? t("receiving address") : t("shipping address"))}
          </h3>
          <button onClick={() => setModalVisible(false)} aria-label={t("Close")}>
            ×
          </button>
        </div>
        <div className="addr-mb">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            <div className="addr-field" style={{ gridColumn: "span 2" }}>
              <label style={fieldLabelStyle}>{t("Label (e.g. Main store)")}</label>
              <Input value={form.Label} onChange={(e) => setForm({ ...form, Label: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 2" }}>
              <label style={fieldLabelStyle}>{t("Contact")}</label>
              <Input value={form.ContactName} onChange={(e) => setForm({ ...form, ContactName: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 4" }}>
              <label style={fieldLabelStyle}>{t("Street address")}</label>
              <Input value={form.Address} onChange={(e) => setForm({ ...form, Address: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 2" }}>
              <label style={fieldLabelStyle}>{t("City")}</label>
              <Input value={form.City} onChange={(e) => setForm({ ...form, City: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 1" }}>
              <label style={fieldLabelStyle}>{t("State")}</label>
              <Input value={form.State} onChange={(e) => setForm({ ...form, State: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 1" }}>
              <label style={fieldLabelStyle}>{t("Postcode")}</label>
              <Input value={form.Zip} onChange={(e) => setForm({ ...form, Zip: e.target.value })} />
            </div>
            <div className="addr-field" style={{ gridColumn: "span 2" }}>
              <label style={fieldLabelStyle}>{t("Country")}</label>
              <Select
                showSearch
                style={{ width: "100%" }}
                optionFilterProp="children"
                value={form.Country}
                onChange={(v) => setForm({ ...form, Country: v })}
              >
                {countryList.map((c) => (
                  <Option key={c.id} value={c.CountryValue}>
                    {c.CountryValue}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="addr-field" style={{ gridColumn: "span 2" }}>
              <label style={fieldLabelStyle}>{t("Phone")}</label>
              <Input value={form.Phone} onChange={(e) => setForm({ ...form, Phone: e.target.value })} />
            </div>
          </div>
          <div style={{ color: "#79838f", fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
            {t("Documents already issued keep the address they were created with.")}
          </div>
        </div>
        <div className="addr-mf">
          <button className="addr-btn neutral" onClick={() => setModalVisible(false)}>
            {t("Cancel")}
          </button>
          <button className="addr-btn" onClick={handleSave} disabled={saving}>
            {t("Save address")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Addresses;
